import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useIntl, FormattedMessage } from "react-intl";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createErrorFeedback,
} from "../../../../context/ui-feedback/action";
import { useSearchTemplates } from "../../../../api/templates-api";
import { Loading } from "../../../common/loading/loading";

export const TemplateSelect = ({ templateId, handleTemplateSelect }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const searchTemplates = useSearchTemplates();

  const { uiFeedbackDispatch } = useUiFeedback();

  const loadedTemplates = useRef(null);
  const [templates, setTemplates] = useState(null);

  useEffect(() => {
    const loadTemplate = async () => {
      createWaiting(uiFeedbackDispatch);
      try {
        const templateSearch = {
          deleted: false,
          draft: false,
          name: null,
          offset: 0,
          limit: 1000,
        };
        await searchTemplates(templateSearch, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            let templateData = data.templates.map((t) => {
              return {
                id: t.id,
                name: t.name,
                template: t,
              };
            });
            templateData.sort((t1, t2) => t1.name.localeCompare(t2.name));
            setTemplates(templateData);
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.serviceworksheets.exception.loadtemplate.title",
            }),
            intl.formatMessage({
              id: "page.serviceworksheets.exception.loadtemplate.description",
            }),
          );
        }
      } finally {
        closeWaiting(uiFeedbackDispatch);
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      loadedTemplates.current === null &&
      templates === null &&
      checkNotEmpy([searchTemplates, intl, navigate, uiFeedbackDispatch])
    ) {
      loadTemplate();
      loadedTemplates.current = new Date();
    }
  }, [
    templates,
    loadedTemplates,
    searchTemplates,
    intl,
    navigate,
    uiFeedbackDispatch,
  ]);

  if (!templates) {
    return <Loading />;
  }

  return (
    <Form.Select
      value={templateId ? templateId : -1}
      onChange={(event) => {
        handleTemplateSelect(
          event.target.value === "-1"
            ? null
            : templates.find((t) => t.id === +event.target.value).template,
        );
      }}
    >
      <option value="-1">
        <FormattedMessage id="data.serviceworksheet.form.templates.value.null" />
      </option>
      {templates.map((template) => (
        <option key={template.id} value={template.id}>
          {template.name}
        </option>
      ))}
    </Form.Select>
  );
};

TemplateSelect.propTypes = {
  templateId: PropTypes.number,
  handleTemplateSelect: PropTypes.func.isRequired,
};
